import { supabase } from '../lib/supabase';

// Function to upload a single image from a URL to Supabase storage
export const uploadImageFromUrl = async (
  imageUrl: string, 
  targetPath: string
): Promise<string | null> => {
  try {
    // Check if the URL is already a Supabase URL
    if (imageUrl.includes(import.meta.env.VITE_SUPABASE_URL)) {
      return imageUrl;
    }

    // First try to use the edge function
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/image-uploader`;
    
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ imageUrl, targetPath })
      });

      if (response.ok) {
        const data = await response.json();
        return data.publicUrl;
      }
    } catch (edgeFunctionError) {
      console.warn('Edge function failed, falling back to client-side upload', edgeFunctionError);
    }

    // Fallback to client-side upload if edge function fails
    // Download the image
    const imageResponse = await fetch(imageUrl);
    if (!imageResponse.ok) {
      throw new Error(`Failed to fetch image: ${imageResponse.statusText}`);
    }
    
    const imageBlob = await imageResponse.blob();
    
    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('vmon')
      .upload(targetPath, imageBlob, {
        cacheControl: '3600',
        upsert: true,
      });

    if (error) {
      throw error;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('vmon')
      .getPublicUrl(targetPath);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    return null;
  }
};

// Function to check if an image already exists in storage
export const checkImageExists = async (path: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.storage
      .from('vmon')
      .download(path);
    
    return !error && data !== null;
  } catch (error) {
    return false;
  }
};

// Function to generate a unique path for an image
export const generateImagePath = (originalUrl: string, folder: string = 'website'): string => {
  // Extract filename from URL
  const urlParts = originalUrl.split('/');
  let filename = urlParts[urlParts.length - 1];
  
  // Clean up filename and ensure it has an extension
  if (!filename.includes('.')) {
    // If no extension, try to guess from URL or default to .jpg
    if (originalUrl.includes('.jpg') || originalUrl.includes('.jpeg')) {
      filename += '.jpg';
    } else if (originalUrl.includes('.png')) {
      filename += '.png';
    } else if (originalUrl.includes('.svg')) {
      filename += '.svg';
    } else if (originalUrl.includes('.gif')) {
      filename += '.gif';
    } else {
      filename += '.jpg';
    }
  }
  
  // Add timestamp to ensure uniqueness
  const timestamp = Date.now();
  const parts = filename.split('.');
  const extension = parts.pop();
  const name = parts.join('.');
  
  return `${folder}/${name}-${timestamp}.${extension}`;
};